// previewCopy.ts
export type PreviewSectionId = 'money' | 'business' | 'team';

export type StemKey =
  | '갑' | '을' | '병' | '정' | '무' | '기' | '경' | '신' | '임' | '계';

export type PreviewLines = {
  fact: string;
  insight: string;
  hook: string;
};

// ⚠️ 규칙: 월/날짜/금액/구체 액션/구체 투자시점 금지 (유료 누수 방지)
// ✅ 목적: "가치 약속 + 궁금증"만 남기는 티저
export const STEM_SECTION_COPY: Record<StemKey, Record<PreviewSectionId, PreviewLines>> = {
  갑: {
    money: {
      fact: '확장 욕구가 강해, 수익보다 비용이 먼저 커지는 패턴이 나오기 쉽습니다.',
      insight: '현금흐름은 "큰 한 방"보다, 고정 수익 라인을 먼저 고정하는 쪽이 유리합니다.',
      hook: '지출이 새는 포인트가 1~2개 잡히면 손익이 즉시 뒤집힙니다(결제 후 공개).',
    },
    business: {
      fact: '개척·확장에 강점이 있어 시장을 먼저 치고 들어갈 때 힘이 납니다.',
      insight: '올해는 "제품/채널"을 늘리기보다, 이기는 조합 1개를 고정하는 게 더 큽니다.',
      hook: '무리한 확장 타이밍이 한 번은 옵니다—판단 기준을 잠금 해제합니다.',
    },
    team: {
      fact: '속도가 빠른 만큼, 느린 파트너/팀과 마찰이 생기기 쉽습니다.',
      insight: '팀은 "속도 담당"과 "품질 담당"을 분리하면 성과가 안정됩니다.',
      hook: '특정 성향 파트너와는 손실이 누적될 수 있어 회피 기준을 제공합니다(결제 후).',
    },
  },

  을: {
    money: {
      fact: '디테일에 강해 누수를 잡으면 바로 성과가 나지만, 결정 지연이 기회를 놓칠 수 있습니다.',
      insight: '수익은 "정교화/고급화"에서 나고, 비용은 "과한 완벽주의"에서 새기 쉽습니다.',
      hook: '돈이 묶이는 구간이 한 번 나오는데, 대응 루틴을 잠금 해제합니다.',
    },
    business: {
      fact: '정리·브랜딩·구조화에 강점이 있어 전환 최적화에 유리합니다.',
      insight: '올해는 제품을 늘리기보다 "패키지/가격/오퍼"를 한 번에 이해되게 만드는 게 핵심입니다.',
      hook: '고객이 빠지는 지점이 잡힙니다—수정안을 결제 후 공개합니다.',
    },
    team: {
      fact: '기준이 높아 팀이 위축되거나, 반대로 존경을 얻기도 합니다.',
      insight: '리뷰/체크리스트를 도입하면 품질은 올라가고 감정 소모는 줄어듭니다.',
      hook: '갈등이 생기는 대화 패턴이 있어 피해야 할 포인트를 제공합니다(결제 후).',
    },
  },

  병: {
    money: {
      fact: '매출을 만드는 힘이 강하지만, 이벤트성 지출이 같이 커질 수 있습니다.',
      insight: '현금흐름은 "반복 노출 → 반복 결제" 구조로 바꿀수록 안정됩니다.',
      hook: '매출은 나는데 통장에 안 남는 원인이 잡히면 바로 개선됩니다(결제 후).',
    },
    business: {
      fact: '홍보/세일즈에서 힘이 나고, 판을 크게 보려는 성향이 있습니다.',
      insight: '올해는 "노출 루틴"을 고정하는 게 성장의 레버리지입니다.',
      hook: '큰 기회가 오지만 함정 조건도 함께 옵니다—필터 기준을 공개합니다.',
    },
    team: {
      fact: '팀을 끌어올리는 추진력이 있지만, 과열되면 번아웃 리스크가 생깁니다.',
      insight: '핵심 인재 1~2명에게 권한을 주고, 본인은 방향/세일즈에 집중하는 게 좋습니다.',
      hook: '사람 때문에 속도가 막히는 구간이 보여 역할 재배치 힌트를 제공합니다(결제 후).',
    },
  },

  정: {
    money: {
      fact: '집중력이 강해 한 방을 만들 수 있지만, 대상이 틀리면 손실도 커질 수 있습니다.',
      insight: '현금흐름은 "핵심 상품 1개"의 단가/마진 개선이 더 빠르게 먹힙니다.',
      hook: '돈이 크게 묶이는 선택지가 보입니다—피해야 할 조건을 결제 후 공개합니다.',
    },
    business: {
      fact: '핵심을 꿰뚫는 힘이 있어 "무엇을 버릴지"를 잘 정하면 급성장합니다.',
      insight: '올해는 확장보다 "집중/정리"가 성과를 더 빨리 만듭니다.',
      hook: '성과가 터지는 포인트가 있지만 준비 없이 들어가면 놓칩니다—준비 체크를 공개합니다.',
    },
    team: {
      fact: '소수 정예와 잘 맞고, 산만한 팀 구조와는 충돌하기 쉽습니다.',
      insight: '역할을 명확히 쪼개면 스트레스가 줄고 속도가 올라갑니다.',
      hook: '함께 하면 손해 보는 관계 타입이 있어 회피 기준을 제공합니다(결제 후).',
    },
  },

  무: {
    money: {
      fact: '안정·자산·기반을 중시해 무리한 확장을 싫어하는 경향이 있습니다.',
      insight: '올해는 "고정비 통제 + 안전마진 확보"가 최우선 전략입니다.',
      hook: '현금흐름이 안전해지는 조건이 보입니다—결제 후 잠금 해제됩니다.',
    },
    business: {
      fact: '운영·인프라·기반을 잘 잡는 타입입니다.',
      insight: '성장은 빠름보다 "안정적 반복"에서 나옵니다. 운영을 시스템화하세요.',
      hook: '안정적으로 커지는 구조 설계가 잡힙니다—전체 설계를 결제 후 공개합니다.',
    },
    team: {
      fact: '책임감이 강해 일을 혼자 떠안기 쉬운 패턴이 있습니다.',
      insight: '업무 분장/권한 위임이 되면 팀이 커져도 흔들리지 않습니다.',
      hook: '팀이 무너지는 트리거가 있어 예방 루틴을 제공합니다(결제 후).',
    },
  },

  기: {
    money: {
      fact: '정리/관리 능력이 좋아 누수를 잡으면 바로 성과가 납니다.',
      insight: '올해는 "자동화/프로세스화"가 곧 돈입니다.',
      hook: '새는 돈을 막는 체크리스트가 있습니다—결제 후 바로 제공합니다.',
    },
    business: {
      fact: '운영·고객 응대·반복 시스템에서 강점이 납니다.',
      insight: '제품을 늘리기보다 고객 여정(문의→결제)을 단순화하면 매출이 오릅니다.',
      hook: '전환이 막히는 구간이 보입니다—그 구간의 개선안을 잠금 해제합니다.',
    },
    team: {
      fact: '조율을 잘하지만, 기준이 없으면 팀이 느려질 수 있습니다.',
      insight: '룰/템플릿/회의 규칙을 정하면 생산성이 급상승합니다.',
      hook: '팀 갈등의 원인(역할 겹침)을 잡아내는 기준을 제공합니다(결제 후).',
    },
  },

  경: {
    money: {
      fact: '결단이 빠르지만 한 번의 큰 결정이 손익을 크게 흔들 수 있습니다.',
      insight: '올해는 지출의 기준을 먼저 세우고, 수익은 그 다음에 키우는 게 안전합니다.',
      hook: '큰 지출 판단에서 피해야 할 신호가 있습니다(결제 후 공개).',
    },
    business: {
      fact: '구조를 바꾸고 방향을 전환하는 데 강점이 있습니다.',
      insight: '애매한 상품/채널을 정리하고, 이기는 라인에 자원을 몰아야 합니다.',
      hook: '한 번의 전환 타이밍이 옵니다—결정 체크를 공개합니다.',
    },
    team: {
      fact: '효율을 중시해 속도는 나지만 감정 충돌이 생길 수 있습니다.',
      insight: '역할/성과 기준을 투명하게 하면 팀의 불만이 줄어듭니다.',
      hook: '충돌하기 쉬운 팀 성향이 있어 채용/협업 회피 기준을 제공합니다(결제 후).',
    },
  },

  신: {
    money: {
      fact: '품질/완성도를 중시해 비용이 올라갈 수 있는 패턴이 있습니다.',
      insight: '올해는 "품질을 유지하면서 마진을 지키는 구조"가 핵심입니다.',
      hook: '원가/시간이 과다 투입되는 구간이 보입니다—개선안을 결제 후 공개합니다.',
    },
    business: {
      fact: '브랜드 신뢰와 결과물 퀄리티로 장기 고객을 만들기 유리합니다.',
      insight: '단기 유행보다 "신뢰의 반복"을 설계하면 꾸준히 커집니다.',
      hook: '브랜드 신뢰를 깨는 행동 패턴이 있어 금지 항목을 제공합니다(결제 후).',
    },
    team: {
      fact: '기준이 높아 인재를 잘 키우지만, 부담을 줄 수도 있습니다.',
      insight: '피드백 루프를 짧게 가져가면 품질과 속도를 둘 다 잡습니다.',
      hook: '팀 퍼포먼스를 깎는 습관이 잡힙니다—교정 루틴을 결제 후 제공합니다.',
    },
  },

  임: {
    money: {
      fact: '흐름을 크게 보며 스케일링 욕구가 큽니다.',
      insight: '올해는 "채널 분산 + 반복 매출"로 현금흐름을 넓히는 게 유리합니다.',
      hook: '확장할수록 누수가 생깁니다—누수 방지 포인트를 결제 후 공개합니다.',
    },
    business: {
      fact: '연결/네트워크/확장에 강점이 있어 판을 키우는 데 유리합니다.',
      insight: '파트너/채널을 늘리되 "핵심 메시지 1개"로 통일해야 전환이 올라갑니다.',
      hook: '기회는 많지만 함정도 많습니다—파트너 필터 기준을 결제 후 공개합니다.',
    },
    team: {
      fact: '사람을 모으는 힘이 있지만, 조직이 느슨해질 수 있습니다.',
      insight: '우선순위/보고/의사결정 규칙을 박으면 속도가 살아납니다.',
      hook: '조직이 흔들리는 트리거가 있어 미리 차단하는 규칙을 제공합니다(결제 후).',
    },
  },

  계: {
    money: {
      fact: '분석을 중시해 준비는 강하지만 실행 타이밍을 놓칠 수 있습니다.',
      insight: '올해는 "지표 기반 실행"을 고정하면 돈이 남기 시작합니다.',
      hook: '돈이 새는 이유가 숫자로 잡힙니다—핵심 지표를 결제 후 공개합니다.',
    },
    business: {
      fact: '관찰/기획/전략 설계에 강점이 있습니다.',
      insight: '작은 실험을 반복하는 구조가 성과를 만듭니다.',
      hook: '실험이 실패로 끝나는 패턴이 있어 실패 방지 체크를 제공합니다(결제 후).',
    },
    team: {
      fact: '원칙이 강하지만 소통이 약해지면 오해가 생길 수 있습니다.',
      insight: '회의·결정 프로토콜을 정하면 팀이 안정됩니다.',
      hook: '협업이 깨지는 대화 포인트가 있습니다—피해야 할 패턴을 결제 후 공개합니다.',
    },
  },
};
